import { FaqFieldDataDto, WebflowFaqDto } from '@/api/generated/model';
import { useWebflowControllerGetFaqs } from '@/api/generated/webflow/webflow';
import { Accordion, Box, IconPlus, Text } from '@cmpsr/components';
import { useMemo } from 'react';
import { text } from '../ppra/components/common/theme';
import { useMediaQuery } from '@chakra-ui/react';

// Define more specific types for the items since the generated types are too generic
interface Faq extends WebflowFaqDto {
  fieldData: FaqFieldDataDto;
}

interface FAQProps {
  variant?: 'light' | 'dark';
}

interface FaqAccordionProps {
  faqs: Faq[];
  textColor: string;
  panelBgColor: string;
  panelHoverBgColor: string;
  answerTextColor: string;
}

const FaqAccordion = ({ faqs, textColor, panelBgColor, panelHoverBgColor, answerTextColor }: FaqAccordionProps) => (
  <Accordion allowMultiple>
    {faqs.map((faq: Faq) => (
      <Accordion.Item
        key={faq.id}
        marginBottom="4"
        backgroundColor={panelBgColor}
        borderRadius="0.5rem"
        borderColor="darkBlue"
        borderWidth="1px"
        borderStyle="solid"
      >
        <Accordion.Button _hover={{ bg: panelHoverBgColor }} color={textColor} padding="15px" {...text.body.semiBold}>
          <Box flex="1" textAlign="left">
            {faq.fieldData?.name || 'Other Questions?'}
          </Box>
          <IconPlus color={textColor} />
        </Accordion.Button>
        <Accordion.Panel>
          <Box color={answerTextColor} px="5px" {...text.body.regular}>
            <Box
              dangerouslySetInnerHTML={{ __html: faq.fieldData?.answer || '' }}
              sx={{
                ul: {
                  paddingLeft: '40px',
                  marginY: '16px',
                },
              }}
            />
          </Box>
        </Accordion.Panel>
      </Accordion.Item>
    ))}
  </Accordion>
);

export const FAQ = ({ variant = 'light' }: FAQProps) => {
  // Fetch all FAQs
  const { data: faqsResponse, isLoading, error } = useWebflowControllerGetFaqs();
  const [isMobile] = useMediaQuery(['(max-width: 992px)']);

  // Process FAQs
  const faqs = useMemo(() => {
    if (!faqsResponse?.items?.flat()) return [] as Faq[];

    // The order of slugs to display FAQs
    const slugOrder = [
      'what-does-a-catch-membership-include',
      'what-cancers-are-included-in-my-risk-assessment',
      'how-does-catch-determine-my-personalized-lifetime-cancer-risk',
      'how-many-risk-factors-does-catch-analyze',
      'how-does-catch-reduce-my-cancer-risk',
      'how-is-my-catch-annual-testing-plan-different-from-the-cancer-screening-guidelines-at-the-doctors-office',
      'is-cancer-actually-preventable',
      'why-is-early-detection-so-important-for-cancer',
      'why-is-catch-an-annual-membership',
      'how-much-time-does-catch-take-is-it-hard',
      'why-is-early-onset-cancer-on-the-rise',
      'what-type-of-recommendations-are-included-in-my-action-plan',
      'how-is-the-catch-membership-full-risk-assessment-different-from-my-preliminary-risk-assessment',
      'how-do-i-cancel-my-membership',
      'does-catch-include-blood-or-genetic-testing',
    ];

    const allFaqs = faqsResponse.items.flat() as Faq[];

    // Filter and sort faqs by the slug order
    return slugOrder.map((slug) => allFaqs.find((faq) => faq.fieldData?.slug === slug)).filter(Boolean) as Faq[];
  }, [faqsResponse]);

  // Split faqs into two halves for 2-column layout, memoized
  const { faqsLeft, faqsRight } = useMemo(() => {
    const mid = Math.ceil(faqs.length / 2);
    return {
      faqsLeft: faqs.slice(0, mid),
      faqsRight: faqs.slice(mid),
    };
  }, [faqs]);

  const textColor = variant === 'dark' ? 'darkBlue' : 'white';
  const panelBgColor = variant === 'dark' ? '#FBFBFB' : 'rgba(255, 255, 255, 0.05)';
  const panelHoverBgColor = variant === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  const answerTextColor = variant === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)';

  if (isLoading) {
    return <Text color={textColor}>Loading FAQs...</Text>;
  }

  if (error) {
    return <Text color={textColor}>Failed to load FAQs.</Text>;
  }

  return (
    <Box
      width="100%"
      mt={8}
      display={{ base: 'block', lg: 'flex' }}
      flexDirection={{ base: 'column', lg: 'row' }}
      gap={{ base: 0, lg: 8 }}
    >
      {/* For mobile, render all in one column. For lg+, split into two columns */}
      <Box flex={1}>
        <FaqAccordion
          faqs={faqs.length === 0 || faqs.length === 1 || isMobile ? faqs : faqsLeft}
          textColor={textColor}
          panelBgColor={panelBgColor}
          panelHoverBgColor={panelHoverBgColor}
          answerTextColor={answerTextColor}
        />
      </Box>
      {/* Only show the right column on lg+ */}
      <Box flex={1} display={{ base: 'none', lg: 'block' }}>
        <FaqAccordion
          faqs={faqsRight}
          textColor={textColor}
          panelBgColor={panelBgColor}
          panelHoverBgColor={panelHoverBgColor}
          answerTextColor={answerTextColor}
        />
      </Box>
    </Box>
  );
};
